import React from "react";
import styled from "styled-components";
import { Form, Formik, FormikHelpers } from "formik";
import * as Sentry from "@sentry/react";
import * as Yup from "yup";
import Recruiter from "../../apiResources/Recruiter";
import Employer from "../../apiResources/Employer";
import RecruiterUser from "../../apiResources/RecruiterUser";
import EmployerUser from "../../apiResources/EmployerUser";
import TextLink from "../library/TextLink";
import { TextInputField } from "../library/Form";
import SubmitButton from "../library/Form/SubmitButton";
import SettingLabel from "./SettingLabel";

const CompanyNameSettingsOption = (props: Props) => {
  const {
    user,
    company,
    isEditing = false,
    disabled = false,
    onEditStart,
    onEditComplete,
  } = props;

  const save = async (
    values: FormValues,
    formik: FormikHelpers<FormValues>
  ) => {
    if (values.name === company.name) {
      onEditComplete();
      return;
    }

    try {
      company.assignAttributes(values);
      const success = await company.save();
      if (success) {
        onEditComplete();
      } else {
        formik.setErrors({
          name: company.errors.name?.fullMessage,
        });
      }
    } catch (err) {
      Sentry.captureException(err);
      formik.setErrors({
        name: "Uh oh, something went wrong there",
      });
    } finally {
      company.rollback();
    }
  };

  const edit = () => {
    if (!disabled) {
      onEditStart();
    }
  };

  if (!isEditing) {
    return (
      <section>
        <Header>
          <SettingLabel>Company Name</SettingLabel>
          {user.isAdmin && (
            <TextLink disabled={disabled} onClick={edit}>
              Edit
            </TextLink>
          )}
        </Header>
        {company.name ? <div>{company.name}</div> : <div>Not specified</div>}
      </section>
    );
  }

  const initialValues: FormValues = { name: company.name };

  const validation = Yup.object({
    name: Yup.string().required("Please enter a company name"),
  });

  return (
    <section>
      <Formik
        initialValues={initialValues}
        onSubmit={save}
        validationSchema={validation}
      >
        <Form autoComplete="off">
          <Header>
            <SettingLabel>Company Name</SettingLabel>
            <TextLink onClick={onEditComplete}>Cancel</TextLink>
          </Header>
          <TextInputField label="Company Name" hiddenLabel name="name" />
          <SubmitButton />
        </Form>
      </Formik>
    </section>
  );
};

type FormValues = {
  name: string;
};

type Props = {
  user: RecruiterUser | EmployerUser;
  company: Recruiter | Employer;
  isEditing?: boolean;
  editable?: boolean;
  disabled?: boolean;
  onEditStart: () => void;
  onEditComplete: () => void;
};

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

export default CompanyNameSettingsOption;
